import { OPPORTUNITY_STAGES, OPPORTUNITY_STAGE_LABELS, type SettingsKey } from '@hearth/shared';
import type { FormSpec } from './SettingsForm';

/** Field layout of every typed settings group (labels in plain sales language, units in the label). */
export const SETTINGS_FORMS: { [K in SettingsKey]: FormSpec<K> } = {
  general: {
    key: 'general',
    title: 'Organization',
    fields: [
      { path: 'companyName', label: 'Company name', kind: 'text', hint: 'Used in email footers and the {{company_name}} variable.' },
      { path: 'defaultCurrency', label: 'Currency', kind: 'text', hint: '3-letter code, e.g. CAD' },
      { path: 'defaultCountry', label: 'Default country', kind: 'text', hint: '2-letter code, e.g. CA' },
      { path: 'defaultTimezone', label: 'Timezone', kind: 'text', hint: 'e.g. America/Toronto' },
      { path: 'locale', label: 'Locale', kind: 'text', hint: 'Number and date format, e.g. en-CA or fr-CA' },
    ],
  },
  compliance: {
    key: 'compliance',
    title: 'Email compliance',
    description: 'Bulk and automated email is blocked until the sender name and mailing address are filled in.',
    fields: [
      {
        path: 'mode',
        label: 'Law you follow',
        kind: 'select',
        options: [
          { value: 'casl', label: 'CASL (Canada) — consent required for bulk/automated email' },
          { value: 'can_spam', label: 'CAN-SPAM (US) — opt-out model' },
          { value: 'none', label: 'None — only unsubscribes and do-not-contact apply' },
        ],
      },
      { path: 'senderLegalName', label: 'Sender legal name', kind: 'text' },
      { path: 'mailingAddress', label: 'Mailing address (in every email footer)', kind: 'textarea' },
      { path: 'unsubscribeText', label: 'Unsubscribe sentence', kind: 'textarea' },
      { path: 'impliedConsentInquiryMonths', label: 'Implied consent after an inquiry lasts (months)', kind: 'number', min: 1, max: 60 },
      { path: 'impliedConsentRelationshipMonths', label: 'Implied consent after a purchase lasts (months)', kind: 'number', min: 1, max: 120 },
    ],
  },
  sending: {
    key: 'sending',
    title: 'Sending limits',
    description: 'Protects your sender reputation. Manual one-off emails respect the daily limits too.',
    fields: [
      { path: 'dailyLimit', label: 'Emails per day (whole company)', kind: 'number', min: 1 },
      { path: 'perUserDailyLimit', label: 'Emails per day (per salesperson)', kind: 'number', min: 1 },
      { path: 'perMinuteLimit', label: 'Emails per minute', kind: 'number', min: 1, max: 1000 },
      { path: 'minDaysBetweenEmails', label: 'Days between bulk/automated emails to one address', kind: 'number', min: 0, max: 365 },
      { path: 'bulkConfirmThreshold', label: 'Type-to-confirm bulk sends from (recipients)', kind: 'number', min: 1 },
      { path: 'maxAttempts', label: 'Delivery attempts before giving up', kind: 'number', min: 1, max: 10 },
      { path: 'sendWindow.enabled', label: 'Only send bulk and automated email during business hours', kind: 'switch' },
      { path: 'sendWindow.startHour', label: 'From (hour, 0–24)', kind: 'number', min: 0, max: 24 },
      { path: 'sendWindow.endHour', label: 'Until (hour, 0–24)', kind: 'number', min: 0, max: 24 },
      { path: 'sendWindow.days', label: 'On these days', kind: 'weekdays' },
      { path: 'respectRecipientTimezone', label: "Use each pizzeria's own timezone for business hours", kind: 'switch' },
      { path: 'trackOpens', label: 'Track opens (only where lawful and disclosed)', kind: 'switch' },
      { path: 'trackClicks', label: 'Track link clicks (only where lawful and disclosed)', kind: 'switch' },
    ],
  },
  automation: {
    key: 'automation',
    title: 'Automation',
    description: 'The master switch for every automated sequence.',
    fields: [
      { path: 'enabled', label: 'Automation is on', kind: 'switch', hint: 'Off = no sequence enrolls anyone or sends anything.' },
      { path: 'runIntervalMinutes', label: 'Run every (minutes)', kind: 'number', min: 5, max: 1440 },
      { path: 'maxEnrollmentsPerRun', label: 'New enrollments per run, at most', kind: 'number', min: 1, max: 5000 },
    ],
  },
  nextAction: {
    key: 'nextAction',
    title: 'Next best action',
    fields: [
      { path: 'replyResponseHours', label: 'Answer replies within (hours)', kind: 'number', min: 1, max: 240 },
      { path: 'quoteFollowUpDays', label: 'Follow up on a sent quote after (days)', kind: 'number', min: 1, max: 90 },
      { path: 'activeFollowUpDays', label: 'Follow up on active conversations after (days)', kind: 'number', min: 1, max: 90 },
      { path: 'priorityTierKeys', label: 'Tiers to contact right away when never contacted', kind: 'multiselect' },
    ],
  },
  opportunities: {
    key: 'opportunities',
    title: 'Deals',
    description: 'Win probability by stage (used for the weighted pipeline) and the reasons offered when a deal is lost.',
    fields: [
      ...OPPORTUNITY_STAGES.map((stage) => ({ path: `stageProbabilities.${stage}`, label: `${OPPORTUNITY_STAGE_LABELS[stage]} (%)`, kind: 'number' as const, min: 0, max: 100 })),
      { path: 'lostReasons', label: 'Lost reasons (one per line)', kind: 'lines' },
    ],
  },
  dataQuality: {
    key: 'dataQuality',
    title: 'Data quality',
    fields: [
      { path: 'staleAfterDays', label: 'Flag lead data as stale after (days without verification)', kind: 'number', min: 30, max: 3650 },
      { path: 'inactiveAfterDays', label: 'Treat a lead as inactive after (days without contact)', kind: 'number', min: 7, max: 3650 },
    ],
  },
  duplicates: {
    key: 'duplicates',
    title: 'Duplicate detection',
    fields: [
      { path: 'possibleThreshold', label: 'Flag as possible duplicate from (% match)', kind: 'number', min: 1, max: 100 },
      { path: 'likelyThreshold', label: 'Treat as the same pizzeria in imports from (% match)', kind: 'number', min: 1, max: 100 },
    ],
  },
  access: {
    key: 'access',
    title: 'Lead access for sales reps',
    description: 'Managers and administrators always see and edit every lead.',
    fields: [
      {
        path: 'repVisibility',
        label: 'Reps can see',
        kind: 'select',
        options: [
          { value: 'all', label: 'Every lead' },
          { value: 'own_and_unassigned', label: 'Their own leads and unassigned leads' },
        ],
      },
      {
        path: 'repEditScope',
        label: 'Reps can edit, email and log activity on',
        kind: 'select',
        options: [
          { value: 'all', label: 'Every lead they can see' },
          { value: 'own_and_unassigned', label: 'Their own leads and unassigned leads' },
        ],
      },
    ],
  },
  security: {
    key: 'security',
    title: 'Sign-in security',
    fields: [
      { path: 'maxFailedLogins', label: 'Lock an account after (failed sign-ins)', kind: 'number', min: 3, max: 50 },
      { path: 'lockoutMinutes', label: 'Lock for (minutes)', kind: 'number', min: 1, max: 1440 },
    ],
  },
};
